import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useRef, useState } from 'react'

import useIsMountedRef from 'use-is-mounted-ref'
import {
    Controller,
    ControllerProps,
    ControllerRenderProps,
    useFormContext
} from '../../../../../Vendor/react-hook-form/dist/index.ie11'
import { CommonFormContext, FormContextType } from '../../../Contexts/CommonFormContext'
import { ReactState, Rules } from '../../../Types/Types'
import Heading, { HeadingLevel } from '../../Heading'
import PopperContent from '../../PopperContent'
import TextInputDropDown from '../../TextInputDropDown'
import { toItemFromSelectItem } from '../ComponentFunctions/SelectFunctions'
import { SelectRangeItemState } from '../Constants'
import { HandleChange, SelectItem, SelectRangeItem } from '../Types'

export type OptionGroup<T> = {
    groupTitle?: string
    options: T[]
}

export type StandardOptionGroup = OptionGroup<SelectItem>

type Props = {
    name?: string
    value?: string
    options: Nullable<StandardOptionGroup>[]
    label: string
    readOnly?: boolean
    rules?: Rules
    defaultValue?: string
    handleChange?: HandleChange
    disabled?: boolean
    labelInputClassName?: string
    variant?: 'outlined' | 'filled' | 'standard'
}

const itemStateMapper = (optionGroup: StandardOptionGroup, newValue: string): StandardOptionGroup => ({
    groupTitle: optionGroup.groupTitle,
    options: optionGroup.options.map((option: SelectItem): SelectItem => {
        if (option.value === newValue) {
            // Update the selected item
            option.state = SelectRangeItemState.INCLUDED
        } else {
            // Update all of the items states to NONE.
            option.state = SelectRangeItemState.NONE
        }

        return option
    })
})

export const GroupedSelect: FC<Props> = ({
    name,
    value,
    options,
    label,
    readOnly,
    rules,
    defaultValue,
    handleChange,
    disabled,
    labelInputClassName,
    variant
}: Props): ReactElement => {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const { watch, setValue, control, trigger } = name
        ? useFormContext()
        : { watch: undefined, setValue: undefined, control: undefined, trigger: undefined }
    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    const textElemRef = useRef(null)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const mapOptionGroups = (opts: Nullable<StandardOptionGroup>[]): StandardOptionGroup[] =>
        opts
            .filter((opt: Nullable<StandardOptionGroup>): boolean => !!opt)
            .map(
                (opt: Nullable<StandardOptionGroup>): StandardOptionGroup =>
                    itemStateMapper(opt as StandardOptionGroup, (watch ? watch(name, '') : value) as string)
            )

    const [optionsState, setOptionsState] = useState<StandardOptionGroup[]>(mapOptionGroups(options))
    const [flatOptions, setFlatOptions]: ReactState<SelectItem[]> = useState<SelectItem[]>([])

    // Update the items based on a string id value.
    const updateItems = (newValue: string): void => {
        if (commonForm?.handleFormChange) {
            commonForm?.handleFormChange(name)
        }
        setOptionsState(mapOptionGroups)
        setIsOpen(false)

        setTimeout((): void => {
            if (isMounted.current) {
                if (handleChange) {
                    handleChange(newValue)
                }

                if (trigger) {
                    trigger(name)
                }
            }
        })
    }

    // What happens when the text field is changed.
    const handleTextFieldChange: (newValue: string) => void = (newValue: string): void => {
        updateItems(newValue)
    }

    // What happens when the user clicks on the text field.
    const handleClickOnTextField: () => void = (): void => {
        setIsOpen((wasOpen) => !wasOpen)
    }

    // What happens when you click on an item in the dropdown box.
    const handleItemClick: (clickedItem: SelectRangeItem | SelectItem) => void = (
        clickedItem: SelectRangeItem | SelectItem
    ): void => {
        updateItems(clickedItem.value)

        if (name && setValue) {
            setValue(name, clickedItem.value)
        } else if (handleChange) {
            handleChange(clickedItem.value)
        }
    }

    const createTextInput = (): ReactElement => {
        if (name && watch) {
            const props: ControllerProps<typeof TextInputDropDown> = {
                control,
                name,
                rules,
                defaultValue: defaultValue ?? watch(name, ''),
                render: ({
                    onChange,
                    value: oldValue
                }: ControllerRenderProps<Record<string, unknown>>): ReactElement => (
                    <>
                        <TextInputDropDown
                            variant={variant}
                            labelInputClassName={labelInputClassName}
                            labelLeft={label}
                            elementRef={textElemRef}
                            customDropdown={true}
                            isOpen={isOpen}
                            value={
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                flatOptions?.find((option: SelectItem): boolean => option?.value === oldValue)?.label ??
                                oldValue
                            }
                            onClick={handleClickOnTextField}
                            onChange={(newValue: string): void => {
                                onChange(newValue, oldValue)
                                handleTextFieldChange(newValue)
                            }}
                            readOnly={readOnly ?? true}
                            disabled={disabled}
                        />
                        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                        <input type='hidden' value={oldValue} />
                    </>
                )
            }

            return <Controller {...props} />
        }

        return (
            <>
                <TextInputDropDown
                    variant={variant}
                    labelInputClassName={labelInputClassName}
                    labelLeft={label}
                    elementRef={textElemRef}
                    customDropdown={true}
                    isOpen={isOpen}
                    value={flatOptions?.find((option: SelectItem): boolean => option?.value === value)?.label ?? value}
                    onClick={handleClickOnTextField}
                    onChange={(newValue: string): void => {
                        handleTextFieldChange(newValue)
                    }}
                    readOnly={readOnly ?? true}
                    disabled={disabled}
                />
                <input type='hidden' value={value} />
            </>
        )
    }

    useEffect((): void => {
        setFlatOptions(
            options.reduce((accum, next): SelectItem[] => accum.concat(next?.options ?? []), [] as SelectItem[])
        )
        setOptionsState(mapOptionGroups(options))
    }, [options])

    return (
        <React.Fragment>
            <div className='select-range-wrapper groups-select'>{createTextInput()}</div>
            {!disabled && (
                <PopperContent
                    className={`grouped-select-options-${variant ?? 'standard'}`}
                    anchorEl={textElemRef.current}
                    open={isOpen}
                    setOpen={setIsOpen}
                    isClickAwayHandled={true}
                >
                    {optionsState.map(
                        (optionGroup: StandardOptionGroup, index: number): ReactElement => (
                            <div className='group-items-wrapper' key={`group-items-wrapper-${index}`}>
                                {optionGroup?.groupTitle && (
                                    <Heading className='giw-group-name' level={HeadingLevel.TWO}>
                                        {optionGroup.groupTitle}
                                    </Heading>
                                )}
                                <div className='items-wrapper'>
                                    {optionGroup.options.map(
                                        (option: SelectItem): ReactElement =>
                                            toItemFromSelectItem(option, handleItemClick, true)
                                    )}
                                </div>
                            </div>
                        )
                    )}
                </PopperContent>
            )}
        </React.Fragment>
    )
}
